import BaseValidator from '~publish/legacy/composer/composer/lib/validation/BaseValidator'
import { getVideoRestrictionsForDraft } from '~publish/legacy/media/config/posts-media-restrictions'
import { validateMedia } from '~publish/legacy/media/validation/validateMedia'
import { VALIDATION_CODE } from '~publish/legacy/validation/constants'
import ValidationFail from '~publish/legacy/validation/ValidationFail'
import type ValidationResult from '~publish/legacy/validation/ValidationResult'
import ValidationSuccess from '~publish/legacy/validation/ValidationSuccess'

import { getRelaxDraftValidationEnabled } from './relaxDraftValidationControl'
import {
  FB_VIDEO_TITLE_TOO_LONG,
  getReelsVideoAspectRatioMessage,
  getReelsVideoTooLongMessage,
  getReelsVideoTooShortMessage,
  getReelsVideoTooSmallDimensionsMessage,
  getVideoFrameRateMessage,
  getVideoResolutionTooSmallMessage,
  getVideoTooLargeMessage,
} from './utils/validationErrorMessages'

// Facebook's video title limit is 255 characters, but multi-byte characters count as more than 1
const FB_VIDEO_TITLE_MAX_BYTES = 255

function getUtf8ByteLength(str: string): number {
  return new TextEncoder().encode(str).length
}

export default class FacebookValidator extends BaseValidator {
  protected isVideoTooLong(maxDuration: number): boolean {
    return (
      this.draft.video !== null &&
      !!maxDuration &&
      this.draft.video.durationMs > maxDuration
    )
  }

  protected isVideoTooShort(minDuration: number): boolean {
    return (
      this.draft.video !== null &&
      !!minDuration &&
      this.draft.video.durationMs < minDuration
    )
  }

  protected isVideoTooBig(maxSize: number): boolean {
    return (
      this.draft.video !== null && !!maxSize && this.draft.video.size > maxSize
    )
  }

  protected isVideoUnderDimensions(
    minWidth: number,
    minHeight: number,
  ): boolean {
    return (
      this.draft.video !== null &&
      !!minWidth &&
      !!minHeight &&
      (this.draft.video.width < minWidth || this.draft.video.height < minHeight)
    )
  }

  protected isVideoWithinAspectRatio(
    minAspectRatio: number,
    maxAspectRatio: number,
  ): boolean {
    return (
      this.draft.video !== null &&
      !!minAspectRatio &&
      !!maxAspectRatio &&
      (this.draft.video.width / this.draft.video.height < minAspectRatio ||
        this.draft.video.width / this.draft.video.height > maxAspectRatio)
    )
  }

  protected isVideoTooSmall(minWidth: number): boolean {
    return this.draft.video !== null && this.draft.video.width < minWidth
  }

  protected isFrameRateIncorrectForVideo(
    minFrameRate: number,
    maxFrameRate: number,
  ): boolean {
    if (this.draft.video !== null && !!this.draft.video.frameRate) {
      const { frameRate } = this.draft.video
      return frameRate < minFrameRate || frameRate > maxFrameRate
    }
    return false
  }

  protected validateAttachmentOrText(): ValidationResult {
    if (this.draft.isFacebookStoryPost()) {
      if (this.draft.hasAttachment()) {
        return new ValidationSuccess()
      } else {
        return new ValidationFail(
          'Stories must contain an attachment',
          VALIDATION_CODE.MISSING_VALUE,
        )
      }
    }

    return super.validateAttachmentOrText()
  }

  protected shouldValidateVideo(): boolean {
    const shouldValidateVideo =
      !!this.draft.service?.videoMaxSizeReels ||
      !!this.draft.service?.videoMinFrameRateReels ||
      !!this.draft.service?.videoMaxFrameRateReels ||
      !!this.draft.service?.videoMinDurationMsReels ||
      !!this.draft.service?.videoMaxDurationMsReels

    return super.shouldValidateVideo() || shouldValidateVideo
  }

  protected validateVideo(): ValidationResult {
    if (this.draft.isFacebookReelPost()) {
      return this.validateReelsVideo()
    }

    if (this.draft.isFacebookStoryPost()) {
      return this.validateStoriesVideo()
    }

    return super.validateVideo()
  }

  protected validateReelsVideo(): ValidationResult {
    const rules = getVideoRestrictionsForDraft(
      this.draft.service.name,
      this.draft.updateType,
    )

    if (!this.draft.video) {
      // A draft holds no queue slot, so it can wait on the video a Reel needs
      // to publish — only the post path (validateUpdate) enforces it. Behind
      // `relax-draft-validation`, same as `BaseValidator.validateDraft`.
      if (this.isDraft && getRelaxDraftValidationEnabled()) {
        return new ValidationSuccess()
      }

      return new ValidationFail(
        'Reels post must contain a video',
        VALIDATION_CODE.MISSING_VALUE,
      )
    }

    if (rules) {
      const error = validateMedia(rules, this.draft.video)

      if (error) {
        return error
      }
    }

    if (
      this.draft.service.videoMaxDurationMsReels &&
      this.isVideoTooLong(this.draft.service.videoMaxDurationMsReels)
    ) {
      const messageOptions = getReelsVideoTooLongMessage(
        (this.draft.service.videoMaxDurationMsReels ?? 0) / 1000,
      )
      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_DURATION,
        messageOptions,
        {
          mediaUrl: this.draft.video?.url,
          mediaType: this.draft.video?.mediaType,
        },
      )
    }

    if (
      this.draft.service.videoMinDurationMsReels &&
      this.isVideoTooShort(this.draft.service.videoMinDurationMsReels)
    ) {
      const messageOptions = getReelsVideoTooShortMessage(
        (this.draft.service.videoMinDurationMsReels ?? 0) / 1000,
      )

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_DURATION,
        messageOptions,
        {
          mediaUrl: this.draft.video?.url,
          mediaType: this.draft.video?.mediaType,
        },
      )
    }

    if (
      this.draft.service?.videoMaxSizeReels &&
      this.isVideoTooBig(this.draft.service?.videoMaxSizeReels)
    ) {
      const maxFileSizeInMb =
        (this.draft.service.videoMaxSizeReels ?? 0) / 1024 / 1024

      const messageOptions = getVideoTooLargeMessage(maxFileSizeInMb)

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_SIZE,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service?.videoMinWidthReels &&
      this.draft.service?.videoMinHeightReels &&
      this.isVideoUnderDimensions(
        this.draft.service.videoMinWidthReels,
        this.draft.service.videoMinHeightReels,
      )
    ) {
      const messageOptions = getReelsVideoTooSmallDimensionsMessage(
        this.draft.service.videoMinWidthReels,
        this.draft.service.videoMinHeightReels,
      )

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.ASPECT_RATIO,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service?.videoMinAspectRatioReels &&
      this.draft.service?.videoMaxAspectRatioReels &&
      this.isVideoWithinAspectRatio(
        this.draft.service.videoMinAspectRatioReels,
        this.draft.service.videoMaxAspectRatioReels,
      )
    ) {
      const messageOptions = getReelsVideoAspectRatioMessage('9/16', '16/9')

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.ASPECT_RATIO,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMinFrameRateReels &&
      this.draft.service.videoMaxFrameRateReels &&
      this.isFrameRateIncorrectForVideo(
        this.draft.service.videoMinFrameRateReels,
        this.draft.service.videoMaxFrameRateReels,
      )
    ) {
      const messageOptions = getVideoFrameRateMessage(
        this.draft.service.videoMinFrameRateReels,
        this.draft.service.videoMaxFrameRateReels,
        this.draft.video?.frameRate ?? 0,
      )

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_FRAME_RATE,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    return new ValidationSuccess()
  }

  protected validateStoriesVideo(): ValidationResult {
    if (
      this.draft.service.videoMaxSizeStories &&
      this.isVideoTooBig(this.draft.service.videoMaxSizeStories)
    ) {
      const messageOptions = getVideoTooLargeMessage(
        this.draft.service.videoMaxSizeStories / 1024 / 1024,
      )
      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_SIZE,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMaxDurationMsStories &&
      this.isVideoTooLong(this.draft.service.videoMaxDurationMsStories)
    ) {
      const messageOptions = getReelsVideoTooLongMessage(
        (this.draft.service.videoMaxDurationMsStories ?? 0) / 1000,
      )
      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_DURATION,
        messageOptions,
        {
          mediaUrl: this.draft.video?.url,
          mediaType: this.draft.video?.mediaType,
        },
      )
    }

    if (
      this.draft.service?.videoMinWidthStories &&
      this.draft.service?.videoMinHeightStories &&
      this.isVideoUnderDimensions(
        this.draft.service.videoMinWidthStories,
        this.draft.service.videoMinHeightStories,
      )
    ) {
      const messageOptions = getReelsVideoTooSmallDimensionsMessage(
        this.draft.service.videoMinWidthStories,
        this.draft.service.videoMinHeightStories,
      )

      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.ASPECT_RATIO,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMinFrameRateStories &&
      this.draft.service.videoMaxFrameRateStories &&
      this.isFrameRateIncorrectForVideo(
        this.draft.service.videoMinFrameRateStories,
        this.draft.service.videoMaxFrameRateStories,
      )
    ) {
      const messageOptions = getVideoFrameRateMessage(
        this.draft.service.videoMinFrameRateStories,
        this.draft.service.videoMaxFrameRateStories,
        this.draft.video?.frameRate ?? 0,
      )
      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_FRAME_RATE,
        messageOptions,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMinResolution &&
      this.isVideoTooSmall(this.draft.service.videoMinResolution)
    ) {
      const message = getVideoResolutionTooSmallMessage(
        this.draft.service.videoMinResolution,
      )

      return new ValidationFail(
        message,
        VALIDATION_CODE.VIDEO_SIZE,
        {
          generic: message,
          specific: message,
        },
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMinAspectRatioStories &&
      this.draft.service.videoMaxAspectRatioStories &&
      this.isVideoWithinAspectRatio(
        this.draft.service.videoMinAspectRatioStories,
        this.draft.service.videoMaxAspectRatioStories,
      )
    ) {
      const message = getReelsVideoAspectRatioMessage(`6/16`, `9/16`)

      return new ValidationFail(
        message.generic,
        VALIDATION_CODE.VIDEO_SIZE,
        message,
        { mediaUrl: this.draft.video?.url },
      )
    }

    if (
      this.draft.service.videoMinDurationMsStories &&
      this.isVideoTooShort(this.draft.service.videoMinDurationMsStories)
    ) {
      const messageOptions = getReelsVideoTooShortMessage(
        (this.draft.service.videoMinDurationMsStories ?? 0) / 1000,
      )
      return new ValidationFail(
        messageOptions.generic,
        VALIDATION_CODE.VIDEO_DURATION,
        messageOptions,
        {
          mediaUrl: this.draft.video?.url,
          mediaType: this.draft.video?.mediaType,
        },
      )
    }

    return new ValidationSuccess()
  }

  protected validateVideoTitle(): ValidationResult {
    // Only validate video title for regular Facebook video posts (not Reels or Stories)
    if (
      this.draft.isFacebookReelPost() ||
      this.draft.isFacebookStoryPost() ||
      !this.draft.video?.name
    ) {
      return new ValidationSuccess()
    }

    if (getUtf8ByteLength(this.draft.video.name) > FB_VIDEO_TITLE_MAX_BYTES) {
      return new ValidationFail(
        FB_VIDEO_TITLE_TOO_LONG,
        VALIDATION_CODE.MAX_LIMIT_REACHED,
      )
    }

    return new ValidationSuccess()
  }

  protected override validateForChannel(): ValidationResult[] {
    return [this.validateVideoTitle()]
  }
}
